import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { MediaDTO } from "@wedding/shared-types";
import { PasswordGatePage } from "./PasswordGatePage.js";
import { Layout } from "../components/common/Layout.js";
import { MediaGrid } from "../components/gallery/MediaGrid.js";
import { Lightbox } from "../components/gallery/Lightbox.js";
import { getEventMedia, downloadZipUrl } from "../api/events.js";

function GalleryContent({ token, eventName }: { token: string; eventName: string }) {
  const [items, setItems] = useState<MediaDTO[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Synchronous guard: `loadingMore` (state) only reflects reality on the
  // *next* render, so two calls to loadMore() within the same tick (e.g.
  // React StrictMode's double-invoked mount effect in dev, or a fast
  // double-trigger of the infinite-scroll sentinel) would both pass the
  // `if (loadingMore) return` check and both fetch+append the same page.
  const loadingRef = useRef(false);
  const cursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(true);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const page = await getEventMedia(token, { cursor: cursorRef.current, limit: 60 });
      setItems((prev) => [...prev, ...page.items]);
      cursorRef.current = page.nextCursor;
      hasMoreRef.current = page.nextCursor !== null;
      setHasMore(hasMoreRef.current);
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [token]);

  useEffect(() => {
    void loadMore();
    // Only run once on mount — subsequent pages are driven by the sentinel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function exitSelectionMode() {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }

  function handleMediaDeleted(mediaId: string) {
    setItems((prev) => prev.filter((m) => m.id !== mediaId));
    setOpenIndex(null);
  }

  return (
    <Layout
      title={eventName}
      actions={
        selectionMode ? (
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <a
              href={selectedIds.size > 0 ? downloadZipUrl(token, [...selectedIds]) : undefined}
              className="btn btn-primary"
              aria-disabled={selectedIds.size === 0}
              style={selectedIds.size === 0 ? { opacity: 0.55, pointerEvents: "none" } : undefined}
              onClick={(e) => {
                if (selectedIds.size === 0) e.preventDefault();
              }}
            >
              Télécharger ({selectedIds.size})
            </a>
            <button type="button" className="btn btn-secondary" onClick={exitSelectionMode}>
              Annuler
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <Link to={`/m/${token}/upload`} className="btn btn-primary">
              Envoyer des photos
            </Link>
            {items.length > 0 && (
              <>
                <button type="button" className="btn btn-secondary" onClick={() => setSelectionMode(true)}>
                  Sélectionner
                </button>
                <a href={downloadZipUrl(token)} className="btn btn-secondary">
                  Tout télécharger
                </a>
              </>
            )}
          </div>
        )
      }
    >
      <MediaGrid
        items={items}
        onOpen={setOpenIndex}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loadingMore={loadingMore}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
      />
      {openIndex !== null && (
        <Lightbox
          items={items}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onNavigate={setOpenIndex}
          onDeleted={handleMediaDeleted}
        />
      )}
    </Layout>
  );
}

export function GalleryPage() {
  const { token } = useParams<{ token: string }>();
  if (!token) return null;

  return (
    <PasswordGatePage token={token}>
      {(event) => <GalleryContent token={token} eventName={event.name} />}
    </PasswordGatePage>
  );
}
